import { useState } from 'react';
import { Menu } from 'lucide-react';
import { AppContentContainer, type ContentMaxWidth, cn } from '@aaas/shared';
import { AtsSidebar } from '../components/AtsSidebar';

export interface AtsLayoutProps {
  children: React.ReactNode;
  /** Maximum width of the content container. Default: '7xl' (1280px) */
  maxWidth?: ContentMaxWidth;
  /** Overflow behavior for main content. Default: 'auto' */
  overflow?: 'auto' | 'hidden';
}

export function AtsLayout({ children, maxWidth = '7xl', overflow = 'auto' }: AtsLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <>
      {/* Sidebar - Hidden on mobile by default, visible on desktop */}
      <AtsSidebar isOpen={isSidebarOpen} onClose={closeSidebar} />

      {/* Main content area with left margin for fixed sidebar on desktop */}
      <div
        className={cn(
          'flex flex-col lg:ml-64',
          overflow === 'hidden' ? 'h-[calc(100vh-4rem)]' : 'min-h-screen'
        )}
      >
        {/* Mobile menu button */}
        <div className="lg:hidden border-b border-gray-200 bg-white px-4 py-3">
          <button
            onClick={toggleSidebar}
            className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            aria-label="Open sidebar"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        {/* Page content */}
        <main className={cn('flex-1 bg-gray-50', overflow === 'hidden' && 'overflow-hidden')}>
          <AppContentContainer
            maxWidth={maxWidth}
            padding={overflow !== 'hidden'}
            className={overflow === 'hidden' ? 'h-full' : undefined}
          >
            {children}
          </AppContentContainer>
        </main>
      </div>
    </>
  );
}
